// src/constants/pricing.js
import { pricingSyncService } from '../services/pricingSyncService';

// Initial default pricing
let currentPricing = {
  free: { 
    INR: 0, 
    USD: 0,
    features: [],
    strikethroughFeatures: []
  },
  paid: {
    INR: { 
      original: 27999, 
      offer: 6999,
      yearly: null,
      yearly_original: null
    },
    USD: { 
      original: 299, 
      offer: 75,
      yearly: null,
      yearly_original: null
    },
    features: [],
    strikethroughFeatures: [],
    deliveryTime: '72-Hour Delivery',
    cta: 'Get Detailed Intelligence',
    note: null,
    best: 'Delivered within 72 Hours',
    badge: 'LAUNCH OFFER',
    highlight: false
  }
};

// Flag to track if we've loaded from Supabase
let isLoaded = false;
let subscribers = [];

// Helper function to transform Supabase data to pricing structure
const transformSupabaseData = (supabaseData) => {
  if (!supabaseData || supabaseData.id !== 'paid') {
    return currentPricing;
  }

  // console.log('Transforming Supabase data:', supabaseData);

  return {
    free: {
      INR: 0,
      USD: 0,
      features: supabaseData.free_features || [],
      strikethroughFeatures: supabaseData.free_strikethrough_features || []
    },
    paid: {
      INR: {
        original: supabaseData.original_price_monthly_inr || 27999,
        offer: supabaseData.price_monthly_inr || 6999,
        yearly: supabaseData.price_yearly_inr || null,
        yearly_original: supabaseData.original_price_yearly_inr || null,
        gst: supabaseData.gst || null
      },
      USD: {
        original: supabaseData.original_price_monthly_usd || 299,
        offer: supabaseData.price_monthly_usd || 75,
        yearly: supabaseData.price_yearly_usd || null,
        yearly_original: supabaseData.original_price_yearly_usd || null
      },
      features: supabaseData.features || [],
      strikethroughFeatures: supabaseData.strikethrough_features || [],
      deliveryTime: supabaseData.delivery_time || '72-Hour Delivery',
      cta: supabaseData.cta || 'Get Detailed Intelligence',
      note: supabaseData.note || null,
      best: supabaseData.best || 'Delivered within 72 Hours',
      badge: supabaseData.badge || 'LAUNCH OFFER',
      highlight: supabaseData.highlight || false
    }
  };
};

// Function to load pricing from Supabase
export const loadPricingFromSupabase = async () => {
  try {
    const supabaseData = await pricingSyncService.fetchPricingFromSupabase();
    // console.log('Fetched from Supabase:', supabaseData);
    
    // If we got data back, transform it
    if (supabaseData) {
      currentPricing = transformSupabaseData(supabaseData);
      // console.log('Updated currentPricing:', currentPricing);
    }
    
    isLoaded = true;
    
    // Notify all subscribers
    subscribers.forEach(callback => callback(currentPricing));
    
    return currentPricing;
  } catch (error) {
    console.error('Failed to load pricing from Supabase:', error);
    return currentPricing;
  }
};

// Function to get current pricing
export const getCurrentPricing = () => {
  return currentPricing;
};

// Subscribe to pricing updates
export const subscribeToPricing = (callback) => {
  subscribers.push(callback);
  
  // If already loaded, call immediately
  if (isLoaded) {
    callback(currentPricing);
  }
  
  // Return unsubscribe function
  return () => {
    subscribers = subscribers.filter(cb => cb !== callback);
  };
};

// Set up real-time subscription
let realtimeSubscription = null;

export const initRealtimePricing = () => {
  if (realtimeSubscription) {
    realtimeSubscription.unsubscribe();
  }
  
  realtimeSubscription = pricingSyncService.subscribeToPricingUpdates((newData) => {
    // console.log('Real-time pricing update received:', newData);
    if (newData) {
      currentPricing = transformSupabaseData(newData);
      isLoaded = true;
      subscribers.forEach(callback => callback(currentPricing));
    }
  });
  
  return realtimeSubscription;
};

// Helper function to get discount percentage
export const getDiscountPercentage = (currency = 'USD', billingCycle = 'monthly') => {
  const pricing = currentPricing.paid;
  const currencyData = pricing[currency];
  
  if (billingCycle === 'yearly' && currencyData.yearly && currencyData.yearly_original) {
    return Math.round(((currencyData.yearly_original - currencyData.yearly) / currencyData.yearly_original) * 100);
  }
  
  if (currencyData.original && currencyData.offer) {
    return Math.round(((currencyData.original - currencyData.offer) / currencyData.original) * 100);
  }
  
  return 0;
};

// Helper function to get savings amount
export const getSavingsAmount = (currency = 'USD', billingCycle = 'monthly') => {
  const pricing = currentPricing.paid;
  const currencyData = pricing[currency];
  
  if (billingCycle === 'yearly' && currencyData.yearly && currencyData.yearly_original) {
    return currencyData.yearly_original - currencyData.yearly;
  }
  
  if (currencyData.original && currencyData.offer) {
    return currencyData.original - currencyData.offer;
  }
  
  return 0;
};

// Helper function to get formatted price
export const getFormattedPrice = (currency = 'USD', billingCycle = 'monthly', showOriginal = false) => {
  const pricing = currentPricing.paid;
  const currencyData = pricing[currency];
  
  let amount;
  if (showOriginal) {
    amount = billingCycle === 'yearly' ? currencyData.yearly_original : currencyData.original;
  } else {
    amount = billingCycle === 'yearly' ? currencyData.yearly : currencyData.offer;
  }
  
  
  if (!amount) return null;
  
  if (currency === 'INR') {
    return `₹${amount.toLocaleString('en-IN')}`;
  }
  return `$${amount}`;
};

// Helper function to get features
export const getFeatures = () => {
  return currentPricing.paid.features || [];
};

// Helper function to get strikethrough features
export const getStrikethroughFeatures = () => {
  return currentPricing.paid.strikethroughFeatures || [];
};

// Helper function to get delivery time
export const getDeliveryTime = () => {
  return currentPricing.paid.deliveryTime || '72-Hour Delivery';
};

// Helper function to get CTA text
export const getCTA = () => {
  return currentPricing.paid.cta || 'Get Detailed Intelligence';
};

// Helper function to get badge
export const getBadge = () => {
  return currentPricing.paid.badge || 'LAUNCH OFFER';
};

// Create a store-like object that always returns the latest pricing
export const PRICE = {
  get free() {
    return currentPricing.free;
  },
  get paid() {
    return currentPricing.paid;
  },
  // For debugging
  get current() {
    return currentPricing;
  }
};

// Auto-initialize when this module loads
const initializePricing = async () => {
  // console.log('Initializing pricing...');
  await loadPricingFromSupabase();
  initRealtimePricing();
  // console.log('Pricing initialized:', currentPricing);
};

// Start initialization immediately
initializePricing().catch(console.error);

// For debugging
console.log('Pricing module loaded, initializing...');


// For backward compatibility, also export as default
export default PRICE;